import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import BrushIcon from "@mui/icons-material/Brush";
import LayersIcon from "@mui/icons-material/Layers";
import PaletteIcon from "@mui/icons-material/Palette";

import {
  RiskFromMetric,
  SceneLayerEntry,
  StoryLayerKey,
} from "../../types/storyMap";
import { useVegetationTypesFor } from "../../api/vegetationType";
import LayerStyleFields from "./LayerStyleFields";
import { hasStyleOverrides, STYLE_CAPABILITIES } from "./layerStyles";
import {
  usePercentileMember,
  useStoryCustomLayers,
  useStoryIgnitionRegimes,
  useStorySimResults,
  useStrategyTiles,
  useStrategyTreatments,
} from "../../api/storyMaps";
import {
  formatWeatherTimestamp,
  simWeatherFromRaw,
  weatherTimestamps,
} from "./storyWeather";
import {
  ADDABLE_LAYER_KEYS,
  entryLabel,
  entryProjectId,
  LAYER_KEY_LABELS,
  newLayerEntry,
  overlapWarnings,
  PROJECT_FREE_KEYS,
  SIM_ENTRY_KEYS,
  TILE_ENTRY_KEYS,
} from "./layerEntries";
import type { ScopeLists } from "./useScopeLists";
import type { StoryScopeInfo } from "./useStoryScope";

/** Built-in colours per key, for the style pickers' "default" state. */
const DEFAULT_COLORS: Record<StoryLayerKey, { color: string; lineColor?: string }> = {
  boundary: { color: "#00d4ad" },
  properties: { color: "#388E3C" },
  treatments: { color: "#ffb74d" },
  assets: { color: "#aaff00" },
  fuel_load: { color: "#e53935" },
  fuel_3d: { color: "#4f7a3a" },
  vegetation: { color: "#cccccc" },
  fire_history: { color: "#808080" },
  ignitions: { color: "#73ff00" },
  custom_layers: { color: "#1abc9c" },
  risk_from: { color: "#EF4444" },
  risk_to: { color: "#ff3b30" },
  inspection: { color: "#ffff00" },
  freehand: { color: "#ff3d6e" },
  weather: { color: "#ffffff" },
};

export interface LayerListOption {
  id: number;
  name: string;
}

export interface LayerListSimulation {
  simulation_id?: string | null;
  name?: string | null;
  is_ensemble?: boolean;
}

const PERCENTILE_OPTIONS = [10, 30, 50, 70, 90];

export type LayerEntriesChange = (
  entries: SceneLayerEntry[],
  opts?: { derived?: boolean }
) => void;

interface SceneLayerListProps {
  entries: SceneLayerEntry[];
  onChange: LayerEntriesChange;
  /** Which projects the story may draw from; folder/root stories pick one per layer. */
  scope: StoryScopeInfo;
  /** Per-project strategies and simulations for the pickers. */
  lists: ScopeLists;
  /** Restrict what "Add layer" offers (default: everything). */
  allowedKeys?: StoryLayerKey[];
  /**
   * Freehand entries: open the on-map drawing tool for this entry. Absent
   * (compare sides) = freehand layers can be styled but not drawn here.
   */
  onDrawEntry?: (entry: SceneLayerEntry) => void;
}

// ---------------------------------------------------------------------------
// Per-key option fields, shared by the add and edit dialogs. All of them edit
// a DRAFT entry via onPatch; nothing commits until the dialog's Save.
// ---------------------------------------------------------------------------

interface EntryConfigProps {
  entry: SceneLayerEntry;
  onPatch: (patch: Partial<SceneLayerEntry>) => void;
  projectId: string | undefined;
  strategies: LayerListOption[];
  simulations: LayerListSimulation[];
}

function StrategySelect({
  value,
  onChange,
  strategies,
}: {
  value: number | null | undefined;
  onChange: (strategyId: number | null) => void;
  strategies: LayerListOption[];
}) {
  return (
    <TextField
      select
      fullWidth
      label="Strategy"
      size="small"
      value={value ?? ""}
      onChange={(e) =>
        onChange(e.target.value === "" ? null : Number(e.target.value))
      }
    >
      <MenuItem value="">None</MenuItem>
      {strategies.map((strategy) => (
        <MenuItem key={strategy.id} value={Number(strategy.id)}>
          {strategy.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

function SimulationSelect({
  value,
  onChange,
  simulations,
}: {
  value: string | null | undefined;
  onChange: (simulationId: string | null) => void;
  simulations: LayerListSimulation[];
}) {
  return (
    <TextField
      select
      fullWidth
      label="Simulation"
      size="small"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <MenuItem value="">None</MenuItem>
      {simulations
        .filter((sim) => !!sim.simulation_id)
        .map((sim) => (
          <MenuItem key={sim.simulation_id} value={sim.simulation_id as string}>
            {sim.name || "Unnamed simulation"}
          </MenuItem>
        ))}
    </TextField>
  );
}

/** Fuel/vegetation options: strategy + date. Tiles resolve after saving. */
function TileEntryConfig({ entry, onPatch, strategies }: EntryConfigProps) {
  return (
    <>
      <StrategySelect
        value={entry.strategyId}
        onChange={(strategyId) => onPatch({ strategyId, tiles: undefined })}
        strategies={strategies}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Date"
          format="YYYY-MM-DD"
          value={entry.date ? dayjs(entry.date) : null}
          onChange={(value) =>
            onPatch({
              date: value ? value.format("YYYY-MM-DD") : null,
              tiles: undefined,
            })
          }
          slotProps={{ textField: { size: "small" } }}
        />
      </LocalizationProvider>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Tiles for this strategy and date are prepared automatically once the
        layer is added — its chip shows the progress.
      </Typography>
    </>
  );
}

type Fuel3DPartKey =
  | "fuel3dCanopy"
  | "fuel3dStems"
  | "fuel3dSurface"
  | "fuel3dGrass"
  | "fuel3dDimBasemap";

const FUEL_3D_PARTS: { key: Fuel3DPartKey; label: string }[] = [
  { key: "fuel3dCanopy", label: "Canopy" },
  { key: "fuel3dStems", label: "Stems" },
  { key: "fuel3dSurface", label: "Surface fuel" },
  { key: "fuel3dGrass", label: "Grass" },
  { key: "fuel3dDimBasemap", label: "Mute the satellite image under the stand" },
];

/**
 * 3D fuel options: the same strategy + date as a fuel-load entry (the trees
 * are planted from those tiles), plus which parts of the stand to draw.
 */
function Fuel3DEntryConfig(props: EntryConfigProps) {
  const { entry, onPatch } = props;
  return (
    <>
      <TileEntryConfig {...props} />
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {FUEL_3D_PARTS.map(({ key, label }) => (
          <FormControlLabel
            key={key}
            control={
              <Switch
                size="small"
                color="secondary"
                checked={entry[key] !== false}
                onChange={(e) => onPatch({ [key]: e.target.checked })}
              />
            }
            label={label}
            slotProps={{ typography: { variant: "body2" } }}
          />
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Trees, shrubs and ground fuel are drawn in 3D from the fuel load. Tilt
        the map to see them stand up.
      </Typography>
    </>
  );
}

/** Treatments options: strategy + type filter + EndDate-year range. */
function TreatmentsEntryConfig({
  entry,
  onPatch,
  projectId,
  strategies,
}: EntryConfigProps) {
  const { data: treatments } = useStrategyTreatments(
    projectId,
    entry.strategyId,
    entry.strategyId != null
  );
  const typeOptions = useMemo(() => {
    const types = new Set<string>();
    (treatments?.features ?? []).forEach((f) => {
      const type = f?.properties?.Type ?? f?.properties?.type;
      if (typeof type === "string" && type) types.add(type);
    });
    return Array.from(types).sort();
  }, [treatments]);

  const isTypeShown = (type: string) =>
    !entry.treatmentTypes?.length || entry.treatmentTypes.includes(type);
  const toggleType = (type: string) => {
    const current = entry.treatmentTypes?.length
      ? entry.treatmentTypes
      : typeOptions;
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onPatch({
      treatmentTypes: next.length >= typeOptions.length ? undefined : next,
    });
  };

  const [fromYear, toYear] = entry.treatmentYearRange ?? [null, null];
  const patchYear = (index: 0 | 1, raw: string) => {
    const year = raw === "" ? null : Number(raw);
    const next: [number | null, number | null] =
      index === 0 ? [year, toYear] : [fromYear, year];
    onPatch({
      treatmentYearRange: next[0] == null && next[1] == null ? undefined : next,
    });
  };

  return (
    <>
      <StrategySelect
        value={entry.strategyId}
        onChange={(strategyId) => onPatch({ strategyId })}
        strategies={strategies}
      />
      {typeOptions.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Treatment types
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {typeOptions.map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isTypeShown(type)}
                    onChange={() => toggleType(type)}
                  />
                }
                label={<Typography variant="caption">{type}</Typography>}
              />
            ))}
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          label="From year"
          size="small"
          type="number"
          value={fromYear ?? ""}
          onChange={(e) => patchYear(0, e.target.value)}
        />
        <TextField
          label="To year"
          size="small"
          type="number"
          value={toYear ?? ""}
          onChange={(e) => patchYear(1, e.target.value)}
        />
      </Box>
    </>
  );
}

function IgnitionsEntryConfig({ entry, onPatch, projectId }: EntryConfigProps) {
  const { data: regimes } = useStoryIgnitionRegimes(projectId, true);
  return (
    <TextField
      select
      fullWidth
      label="Ignition regime"
      size="small"
      value={entry.ignitionRegimeId ?? ""}
      onChange={(e) =>
        onPatch({
          ignitionRegimeId:
            e.target.value === "" ? null : Number(e.target.value),
        })
      }
    >
      <MenuItem value="">None</MenuItem>
      {(regimes ?? []).map((regime) => (
        <MenuItem key={regime.id} value={regime.id}>
          {regime.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

function CustomLayersEntryConfig({ entry, onPatch, projectId }: EntryConfigProps) {
  const { data: customLayers } = useStoryCustomLayers(projectId, true);
  const selected = entry.customLayerIds ?? [];
  const toggle = (layerId: number) =>
    onPatch({
      customLayerIds: selected.includes(layerId)
        ? selected.filter((id) => id !== layerId)
        : [...selected, layerId],
    });

  if ((customLayers ?? []).length === 0) {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        This project has no custom layers.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {(customLayers ?? []).map((layer) => (
        <FormControlLabel
          key={layer.id}
          control={
            <Checkbox
              size="small"
              color="secondary"
              checked={selected.includes(layer.id)}
              onChange={() => toggle(layer.id)}
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: layer.color ?? "#1abc9c",
                  opacity: layer.opacity ?? 0.4,
                  border: "1px solid rgba(148, 163, 184, 0.4)",
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption">{layer.name}</Typography>
            </Box>
          }
        />
      ))}
    </Box>
  );
}

function SimEntryConfig(props: EntryConfigProps) {
  const { entry, onPatch, simulations, projectId } = props;
  const selectedSim = simulations.find(
    (s) => s.simulation_id === entry.simulationId
  );
  const isEnsemble = !!selectedSim?.is_ensemble;
  const inspectionView = entry.ensembleView ?? "confidence";

  // Percentile-scoped ensemble inspection: resolve which member sits at the
  // chosen percentile (mirrors animation entries; the freeze re-resolves).
  const wantsPercentile =
    entry.key === "inspection" && isEnsemble && inspectionView === "percentile";
  const { data: percentileMember } = usePercentileMember(
    projectId,
    entry.simulationId,
    entry.percentile ?? 50,
    wantsPercentile
  );
  const resolvedMember = percentileMember?.member;
  useEffect(() => {
    if (!wantsPercentile || resolvedMember == null) return;
    if (entry.resolvedMember === resolvedMember) return;
    onPatch({ resolvedMember });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsPercentile, resolvedMember, entry.resolvedMember]);

  return (
    <>
      <SimulationSelect
        value={entry.simulationId}
        onChange={(simulationId) =>
          onPatch({ simulationId, resolvedMember: undefined })
        }
        simulations={simulations}
      />
      {entry.key === "inspection" && isEnsemble && (
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Scenarios
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="secondary"
            value={inspectionView}
            onChange={(_, value: "percentile" | "confidence" | null) => {
              if (value) {
                onPatch({ ensembleView: value, resolvedMember: undefined });
              }
            }}
            sx={{ display: "flex", mt: 0.25 }}
          >
            <ToggleButton value="confidence" sx={{ flex: 1 }}>
              All members
            </ToggleButton>
            <ToggleButton value="percentile" sx={{ flex: 1 }}>
              Percentile member
            </ToggleButton>
          </ToggleButtonGroup>
          {inspectionView === "percentile" && (
            <TextField
              select
              fullWidth
              label="Percentile"
              size="small"
              sx={{ mt: 1 }}
              value={entry.percentile ?? 50}
              onChange={(e) =>
                onPatch({
                  percentile: Number(e.target.value),
                  resolvedMember: undefined,
                })
              }
            >
              {PERCENTILE_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  P{p}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      )}
      {entry.key === "risk_from" && (
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Metric
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="secondary"
            value={entry.riskFromMetric ?? "fire_size"}
            onChange={(_, value: RiskFromMetric | null) => {
              if (value) onPatch({ riskFromMetric: value });
            }}
            sx={{ display: "flex", mt: 0.25 }}
          >
            <ToggleButton value="fire_size" sx={{ flex: 1 }}>
              Fire size
            </ToggleButton>
            <ToggleButton value="impacted_assets" sx={{ flex: 1 }}>
              Impacted assets
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      {entry.key === "inspection" && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          After adding, click the map to set the inspection point — yellow
          cells are ignitions whose fire reaches it. Without a point, every
          ignition origin shows.
        </Typography>
      )}
    </>
  );
}

function FreehandEntryConfig({
  entry,
  onDraw,
}: EntryConfigProps & { onDraw?: () => void }) {
  const count = entry.features?.features.length ?? 0;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {count === 0
          ? "Nothing drawn yet. Draw lines and polygons on the map with their own colours, line styles and widths."
          : `${count} drawn feature${count === 1 ? "" : "s"}.`}
      </Typography>
      {onDraw ? (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<BrushIcon />}
          onClick={onDraw}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          {count === 0 ? "Draw on map" : "Edit drawing"}
        </Button>
      ) : (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Draw this layer from the scene's main map first, then add it here.
        </Typography>
      )}
    </Box>
  );
}

/** Legend label override + hide toggle, offered for every layer type. */
function LegendFields({ entry, onPatch }: EntryConfigProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
        Legend
      </Typography>
      <TextField
        size="small"
        label="Legend label"
        placeholder={LAYER_KEY_LABELS[entry.key]}
        value={entry.label ?? ""}
        onChange={(e) => onPatch({ label: e.target.value || undefined })}
      />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            color="secondary"
            checked={!entry.hideFromLegend}
            onChange={(e) => onPatch({ hideFromLegend: e.target.checked ? undefined : true })}
          />
        }
        label={<Typography variant="caption">Show in legend</Typography>}
      />
    </Box>
  );
}

/** Weather options: simulation, hour, and which parts to draw. */
function WeatherEntryConfig({ entry, onPatch, projectId, simulations }: EntryConfigProps) {
  const { data: results } = useStorySimResults(projectId, entry.simulationId, !!entry.simulationId);
  const weather = useMemo(() => {
    const first = Array.isArray(results) ? (results[0] as Record<string, unknown>) : null;
    return first ? simWeatherFromRaw(first) : null;
  }, [results]);
  const stamps = useMemo(() => weatherTimestamps(weather), [weather]);
  const hasGrid = !!weather?.grid;
  return (
    <>
      <SimulationSelect
        value={entry.simulationId}
        onChange={(simulationId) => onPatch({ simulationId, weatherTime: undefined })}
        simulations={simulations}
      />
      {entry.simulationId && stamps.length > 0 && (
        <TextField
          select
          fullWidth
          size="small"
          label="Hour"
          value={entry.weatherTime && stamps.includes(entry.weatherTime) ? entry.weatherTime : stamps[0]}
          onChange={(e) => onPatch({ weatherTime: e.target.value })}
          helperText={
            hasGrid
              ? "Spatial weather grid: values vary across the map."
              : "Uniform weather: the same values everywhere."
          }
        >
          {stamps.map((ts) => (
            <MenuItem key={ts} value={ts}>
              {formatWeatherTimestamp(ts)}
            </MenuItem>
          ))}
        </TextField>
      )}
      {entry.simulationId && results && stamps.length === 0 && (
        <Typography variant="caption" sx={{ color: "warning.main" }}>
          This simulation has no stored weather.
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              color="secondary"
              checked={entry.weatherShowWind !== false}
              onChange={(e) => onPatch({ weatherShowWind: e.target.checked ? undefined : false })}
            />
          }
          label={<Typography variant="caption">Animated wind particles</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              color="secondary"
              checked={entry.weatherShowLabels !== false}
              onChange={(e) => onPatch({ weatherShowLabels: e.target.checked ? undefined : false })}
            />
          }
          label={<Typography variant="caption">Wind arrows on the map</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              color="secondary"
              checked={entry.weatherShowPanel !== false}
              onChange={(e) => onPatch({ weatherShowPanel: e.target.checked ? undefined : false })}
            />
          }
          label={
            <Typography variant="caption">
              Readings panel (temperature, humidity, wind)
            </Typography>
          }
        />
      </Box>
      {entry.weatherShowPanel !== false && (
        <ToggleButtonGroup
          exclusive
          size="small"
          color="secondary"
          value={entry.weatherPanelSide ?? "right"}
          onChange={(_, value: "left" | "right" | null) => {
            if (value) onPatch({ weatherPanelSide: value === "right" ? undefined : value });
          }}
          sx={{ display: "flex" }}
        >
          <ToggleButton value="left" sx={{ flex: 1 }}>
            Panel on left
          </ToggleButton>
          <ToggleButton value="right" sx={{ flex: 1 }}>
            Panel on right
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        In an animation scene the weather follows the playback clock instead
        of the hour chosen here. Wind particles animate on screen only — the
        PDF shows the arrows and the readings.
      </Typography>
    </>
  );
}

function EntryConfigFields(props: EntryConfigProps & { onDraw?: () => void }) {
  const { entry } = props;
  let config: React.ReactNode;
  if (entry.key === "weather") config = <WeatherEntryConfig {...props} />;
  else if (entry.key === "fuel_3d") config = <Fuel3DEntryConfig {...props} />;
  else if (TILE_ENTRY_KEYS.includes(entry.key)) config = <TileEntryConfig {...props} />;
  else if (entry.key === "treatments") config = <TreatmentsEntryConfig {...props} />;
  else if (entry.key === "ignitions") config = <IgnitionsEntryConfig {...props} />;
  else if (entry.key === "custom_layers") config = <CustomLayersEntryConfig {...props} />;
  else if (SIM_ENTRY_KEYS.includes(entry.key)) config = <SimEntryConfig {...props} />;
  else if (entry.key === "freehand") config = <FreehandEntryConfig {...props} />;
  else config = null;
  const styleable = STYLE_CAPABILITIES[entry.key].length > 0;
  return (
    <>
      {config}
      {styleable && (
        <LayerStyleFields
          layerKey={entry.key}
          style={entry.style}
          onChange={(style) => props.onPatch({ style })}
          defaults={DEFAULT_COLORS[entry.key]}
        />
      )}
      <LegendFields {...props} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Add/edit dialog: pick a layer type (add only), set its options, commit.
// ---------------------------------------------------------------------------

interface LayerEntryDialogProps {
  open: boolean;
  mode: "add" | "edit";
  initial: SceneLayerEntry | null;
  addableKeys: StoryLayerKey[];
  onCancel: () => void;
  onSave: (entry: SceneLayerEntry) => void;
  /** Freehand: commit the draft and open the drawing tool. */
  onSaveAndDraw?: (entry: SceneLayerEntry) => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
}

/** Per-key settings that depend on the project; they go when the project changes. */
const PROJECT_BOUND_FIELDS: Partial<SceneLayerEntry> = {
  strategyId: undefined,
  date: undefined,
  tiles: undefined,
  tilesStale: undefined,
  treatmentTypes: undefined,
  treatmentYearRange: undefined,
  ignitionRegimeId: undefined,
  customLayerIds: undefined,
  simulationId: undefined,
  inspectionPoint: undefined,
  resolvedMember: undefined,
  weatherTime: undefined,
  legendItems: undefined,
};

/** The lists a dialog or chip reads for one entry's project. */
function listsFor(lists: ScopeLists, projectId: number | null) {
  const key = projectId !== null ? String(projectId) : "";
  return {
    projectId: projectId !== null ? String(projectId) : undefined,
    strategies: lists.strategiesByProject[key] ?? EMPTY_STRATEGIES,
    simulations: lists.simulationsByProject[key] ?? EMPTY_SIMULATIONS,
  };
}
const EMPTY_STRATEGIES: LayerListOption[] = [];
const EMPTY_SIMULATIONS: LayerListSimulation[] = [];

function LayerEntryDialog({
  open,
  mode,
  initial,
  addableKeys,
  onCancel,
  onSave,
  onSaveAndDraw,
  scope,
  lists,
}: LayerEntryDialogProps) {
  const [draft, setDraft] = useState<SceneLayerEntry | null>(initial);
  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);
  const draftProjectId = draft ? entryProjectId(draft, scope.defaultProjectId) : null;
  // The pickers for the chosen project load on demand.
  useEffect(() => {
    if (open && draftProjectId !== null) lists.requestProject(draftProjectId);
  }, [open, draftProjectId, lists]);

  if (!draft) return null;
  const patch = (p: Partial<SceneLayerEntry>) =>
    setDraft((d) => (d ? { ...d, ...p } : d));
  const needsProject = scope.isScoped && !PROJECT_FREE_KEYS.includes(draft.key);
  const projectKnown = !needsProject || (draftProjectId !== null && scope.byId.has(draftProjectId));
  const per = listsFor(lists, draftProjectId);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>
        {mode === "add" ? "Add layer to map" : entryLabel(draft)}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: "8px !important" }}
      >
        {mode === "add" && (
          <TextField
            select
            fullWidth
            label="Layer"
            size="small"
            value={draft.key}
            onChange={(e) =>
              // Changing type resets the draft: options don't carry across.
              // The project carries over — it is where the author is working.
              setDraft({
                ...newLayerEntry(e.target.value as StoryLayerKey),
                id: draft.id,
                ...(draft.projectId != null ? { projectId: draft.projectId } : {}),
              })
            }
          >
            {addableKeys.map((key) => (
              <MenuItem key={key} value={key}>
                {LAYER_KEY_LABELS[key]}
              </MenuItem>
            ))}
          </TextField>
        )}
        {needsProject && (
          <TextField
            select
            fullWidth
            label="Project"
            size="small"
            value={draftProjectId !== null && scope.byId.has(draftProjectId) ? draftProjectId : ""}
            onChange={(e) => {
              const projectId = e.target.value === "" ? null : Number(e.target.value);
              // Strategies, simulations, regimes and layers belong to a
              // project: a new project means choosing them again.
              patch({ ...PROJECT_BOUND_FIELDS, projectId });
            }}
            helperText={
              draftProjectId !== null && !scope.byId.has(draftProjectId)
                ? "This project is no longer in the story's folder. Pick another."
                : scope.kind === "folder"
                  ? "Any project in this folder, or a folder inside it."
                  : "Any project in your organisation."
            }
          >
            <MenuItem value="">Choose a project…</MenuItem>
            {scope.universe.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        {projectKnown ? (
          <EntryConfigFields
            entry={draft}
            onPatch={patch}
            projectId={per.projectId}
            strategies={per.strategies}
            simulations={per.simulations}
            onDraw={onSaveAndDraw ? () => onSaveAndDraw(draft) : undefined}
          />
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Pick the project this layer comes from to see its options.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!projectKnown}
          onClick={() => onSave(draft)}
        >
          {mode === "add" ? "Add to map" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Chips: one per configured layer — summary label, click to edit, X to
// remove. Fuel/veg chips also own their entry's tile resolution.
// ---------------------------------------------------------------------------

interface LayerChipProps {
  entry: SceneLayerEntry;
  onClick: () => void;
  onDelete: () => void;
  onDerivedPatch: (patch: Partial<SceneLayerEntry>) => void;
  scope: StoryScopeInfo;
  lists: ScopeLists;
  /** Other projects whose same-kind opaque layer overlaps this one's area. */
  overlaps?: string[];
}

/**
 * Vegetation chips persist the class → colour rows the legend lists (derived,
 * like tiles), so published stories carry them for anonymous readers.
 */
function useVegetationLegendItems(
  entry: SceneLayerEntry,
  onDerivedPatch: (patch: Partial<SceneLayerEntry>) => void,
  projectId: string | undefined
) {
  const isVegetation = entry.key === "vegetation";
  const { data: vegTypes } = useVegetationTypesFor(isVegetation ? projectId : null);
  const items = useMemo(() => {
    if (!isVegetation) return null;
    const rows = (vegTypes?.data ?? [])
      .filter((t) => !!t.color)
      .map((t) => ({ label: t.veg_class, color: t.color as string }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return rows.length ? rows : null;
  }, [isVegetation, vegTypes]);
  const serialised = items ? JSON.stringify(items) : null;
  useEffect(() => {
    if (!isVegetation || !items || !serialised) return;
    if (JSON.stringify(entry.legendItems ?? null) === serialised) return;
    onDerivedPatch({ legendItems: items });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVegetation, serialised, entry.id]);
}

function LayerChip({
  entry,
  onClick,
  onDelete,
  onDerivedPatch,
  scope,
  lists,
  overlaps,
}: LayerChipProps) {
  const entryPid = entryProjectId(entry, scope.defaultProjectId);
  const projectKnown = !scope.isScoped || PROJECT_FREE_KEYS.includes(entry.key)
    ? true
    : entryPid !== null && scope.byId.has(entryPid);
  const { projectId, strategies, simulations } = listsFor(lists, projectKnown ? entryPid : null);
  // Chips name strategies and simulations; make sure their project's lists load.
  useEffect(() => {
    if (projectKnown && entryPid !== null && !PROJECT_FREE_KEYS.includes(entry.key)) {
      lists.requestProject(entryPid);
    }
  }, [projectKnown, entryPid, entry.key, lists]);
  useVegetationLegendItems(entry, onDerivedPatch, projectId);
  const isTileKey = TILE_ENTRY_KEYS.includes(entry.key);
  const { tiles, isResolving, error } = useStrategyTiles(
    projectId,
    entry.strategyId ?? null,
    entry.date ?? null,
    isTileKey
  );

  // The chip owns tile resolution for its entry: the dialog only edits the
  // draft, so resolved URLs are persisted here (derived — resolving on open
  // must not read as unsaved authoring).
  const fuelUrl = tiles?.fuelTilesUrl;
  const vegUrl = tiles?.vegetationTilesUrl;
  useEffect(() => {
    if (!isTileKey || !tiles) return;
    if (
      entry.tiles?.fuelTilesUrl === fuelUrl &&
      entry.tiles?.vegetationTilesUrl === vegUrl
    ) {
      return;
    }
    onDerivedPatch({
      tiles: {
        fuelTilesUrl: fuelUrl,
        vegetationTilesUrl: vegUrl,
        resolvedAt: new Date().toISOString(),
      },
      tilesStale: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuelUrl, vegUrl, entry.id]);

  const { data: regimes } = useStoryIgnitionRegimes(
    projectId,
    entry.key === "ignitions" && entry.ignitionRegimeId != null
  );

  const strategyName = (id: number | null | undefined) =>
    strategies.find((s) => s.id === Number(id))?.name;
  const simulationName = (id: string | null | undefined) =>
    simulations.find((s) => s.simulation_id === id)?.name || "Unnamed simulation";

  // detail = the summary after the layer name; incomplete = missing config.
  let detail: string | null = null;
  let incomplete = false;
  switch (entry.key) {
    case "treatments": {
      if (entry.strategyId == null) {
        detail = "choose a strategy";
        incomplete = true;
        break;
      }
      const parts = [strategyName(entry.strategyId) ?? `#${entry.strategyId}`];
      if (entry.treatmentTypes?.length) {
        parts.push(`${entry.treatmentTypes.length} types`);
      }
      const [from, to] = entry.treatmentYearRange ?? [null, null];
      if (from != null || to != null) parts.push(`${from ?? "…"}–${to ?? "…"}`);
      detail = parts.join(" · ");
      break;
    }
    case "fuel_load":
    case "fuel_3d":
    case "vegetation": {
      if (entry.strategyId == null || !entry.date) {
        detail = "choose strategy & date";
        incomplete = true;
        break;
      }
      detail = `${strategyName(entry.strategyId) ?? `#${entry.strategyId}`} · ${entry.date}`;
      break;
    }
    case "ignitions": {
      if (entry.ignitionRegimeId == null) {
        detail = "choose a regime";
        incomplete = true;
        break;
      }
      detail =
        (regimes ?? []).find((r) => r.id === entry.ignitionRegimeId)?.name ??
        `regime #${entry.ignitionRegimeId}`;
      break;
    }
    case "custom_layers": {
      const count = entry.customLayerIds?.length ?? 0;
      if (count === 0) {
        detail = "choose layers";
        incomplete = true;
      } else {
        detail = `${count} layer${count === 1 ? "" : "s"}`;
      }
      break;
    }
    case "risk_from": {
      if (!entry.simulationId) {
        detail = "choose a simulation";
        incomplete = true;
        break;
      }
      detail = `${simulationName(entry.simulationId)} · ${
        entry.riskFromMetric === "impacted_assets" ? "Impacted assets" : "Fire size"
      }`;
      break;
    }
    case "risk_to":
    case "inspection": {
      if (!entry.simulationId) {
        detail = "choose a simulation";
        incomplete = true;
        break;
      }
      detail = simulationName(entry.simulationId);
      if (entry.key === "inspection") {
        const isEnsembleSim = !!simulations.find(
          (s) => s.simulation_id === entry.simulationId
        )?.is_ensemble;
        if (isEnsembleSim) {
          detail +=
            entry.ensembleView === "percentile"
              ? ` · P${entry.percentile ?? 50} member`
              : " · all members";
        }
        detail += entry.inspectionPoint ? " · point set" : " · click map to set point";
      }
      break;
    }
    case "weather": {
      if (!entry.simulationId) {
        detail = "choose a simulation";
        incomplete = true;
        break;
      }
      detail = simulationName(entry.simulationId);
      if (entry.weatherTime) detail += ` · ${formatWeatherTimestamp(entry.weatherTime)}`;
      break;
    }
    case "freehand": {
      const count = entry.features?.features.length ?? 0;
      if (count === 0) {
        detail = "nothing drawn yet";
        incomplete = true;
      } else {
        detail = `${count} feature${count === 1 ? "" : "s"}`;
      }
      break;
    }
    default:
      break;
  }

  // Folder and organisation-wide stories: the chip leads with the project,
  // and an entry whose project is missing (never picked, or moved out of the
  // folder since) is flagged like any other incomplete entry.
  let prefix = "";
  if (scope.isScoped && !PROJECT_FREE_KEYS.includes(entry.key)) {
    if (!projectKnown) {
      detail = entryPid === null ? "pick a project" : "project no longer in this story";
      incomplete = true;
    } else {
      prefix = `${scope.byId.get(entryPid as number)?.name ?? "Project"} · `;
    }
  }

  const label = `${prefix}${detail ? `${entryLabel(entry)} · ${detail}` : entryLabel(entry)}`;
  const tileTrouble = isTileKey && !incomplete && !isResolving && !!error;
  const styled = hasStyleOverrides(entry.style);
  const overlapping = !!overlaps?.length;

  const chip = (
    <Chip
      size="small"
      label={label}
      onClick={onClick}
      onDelete={onDelete}
      icon={
        isTileKey && isResolving ? (
          <CircularProgress size={12} />
        ) : overlapping ? (
          <LayersIcon sx={{ fontSize: 14 }} />
        ) : entry.key === "freehand" ? (
          <BrushIcon sx={{ fontSize: 14 }} />
        ) : styled ? (
          <PaletteIcon sx={{ fontSize: 14, color: `${entry.style?.color ?? entry.style?.lineColor ?? "#00d4ad"} !important` }} />
        ) : undefined
      }
      color={incomplete || tileTrouble || overlapping ? "warning" : "default"}
      variant={incomplete || tileTrouble ? "outlined" : "filled"}
      sx={{ justifyContent: "space-between", maxWidth: "100%" }}
    />
  );
  if (!overlapping) return chip;
  return (
    <Tooltip
      title={`Overlaps ${overlaps!.join(", ")}. Where two projects' ${LAYER_KEY_LABELS[entry.key].toLowerCase()} overlap, the layer further down this list draws on top.`}
    >
      <span style={{ display: "inline-flex", maxWidth: "100%" }}>{chip}</span>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// The list itself.
// ---------------------------------------------------------------------------

/**
 * Layer editor over a SceneLayerEntry list: an "Add layer" button opens a
 * dialog with that layer type's options; added layers render as compact
 * summary chips — click to edit, X to remove. Shared by the scene rail and
 * both compare-mode sides.
 */
export default function SceneLayerList({
  entries,
  onChange,
  scope,
  lists,
  allowedKeys,
  onDrawEntry,
}: SceneLayerListProps) {
  const addable = ADDABLE_LAYER_KEYS.filter(
    (key) => !allowedKeys || allowedKeys.includes(key)
  );
  // A new layer in a folder story starts on the project the author last
  // worked with: the previous entry's, else the first in the folder.
  const lastProjectId = useMemo(() => {
    if (!scope.isScoped) return null;
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      const pid = entries[i].projectId;
      if (pid != null && scope.byId.has(Number(pid))) return Number(pid);
    }
    return scope.universe[0]?.id ?? null;
  }, [entries, scope]);
  const overlaps = useMemo(
    () => overlapWarnings(entries, scope.byId, scope.defaultProjectId),
    [entries, scope.byId, scope.defaultProjectId]
  );
  const [dialog, setDialog] = useState<
    | { mode: "add"; initial: SceneLayerEntry }
    | { mode: "edit"; initial: SceneLayerEntry }
    | null
  >(null);

  const patchEntry = (
    entryId: string,
    patch: Partial<SceneLayerEntry>,
    opts?: { derived?: boolean }
  ) =>
    onChange(
      entries.map((e) => (e.id === entryId ? { ...e, ...patch } : e)),
      opts
    );

  const handleSave = (entry: SceneLayerEntry) => {
    setDialog(null);
    if (entries.some((e) => e.id === entry.id)) {
      onChange(entries.map((e) => (e.id === entry.id ? entry : e)));
    } else {
      onChange([...entries, entry]);
    }
  };
  const handleSaveAndDraw = (entry: SceneLayerEntry) => {
    handleSave(entry);
    onDrawEntry?.(entry);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {entries.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {entries.map((entry) => (
            <LayerChip
              key={entry.id}
              entry={entry}
              onClick={() => setDialog({ mode: "edit", initial: entry })}
              onDelete={() => onChange(entries.filter((e) => e.id !== entry.id))}
              onDerivedPatch={(patch) =>
                patchEntry(entry.id, patch, { derived: true })
              }
              scope={scope}
              lists={lists}
              overlaps={overlaps.get(entry.id)}
            />
          ))}
        </Box>
      )}
      <Button
        size="small"
        variant="outlined"
        color="secondary"
        startIcon={<AddIcon />}
        onClick={() =>
          setDialog({
            mode: "add",
            initial: {
              ...newLayerEntry(addable[0]),
              ...(lastProjectId !== null ? { projectId: lastProjectId } : {}),
            },
          })
        }
        sx={{ alignSelf: "flex-start", textTransform: "none" }}
      >
        Add layer
      </Button>
      <LayerEntryDialog
        open={!!dialog}
        mode={dialog?.mode ?? "add"}
        initial={dialog?.initial ?? null}
        addableKeys={addable}
        onCancel={() => setDialog(null)}
        onSave={handleSave}
        onSaveAndDraw={onDrawEntry ? handleSaveAndDraw : undefined}
        scope={scope}
        lists={lists}
      />
    </Box>
  );
}
